import React, { useMemo, useState } from 'react';
import { useData } from '../../data/DataProvider';
import { useToast } from '../../shared/components/ToastProvider';
import EmptyState from '../../shared/components/EmptyState';
import SlideOver from '../../shared/components/SlideOver';
import ConfirmDialog from '../../shared/components/ConfirmDialog';
import FileDropzone from '../../shared/components/FileDropzone';
import SafeIcon from '../../common/SafeIcon';
import { Icons } from '../../shared/icons';
import { date } from '../../shared/formatters';
import { openProtectedFile } from '../../lib/pb';

const DOC_TYPES = ['pdf', 'doc', 'csv', 'parts_list', 'manual', 'policy', 'other'];
const STATUSES = ['draft', 'active', 'archived'];
const FILE_ACCEPT = 'application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

const EMPTY_FORM = {
  title: '', description: '', category: '', document_type: 'other',
  version: '', effective_date: '', expiry_date: '', tags: '',
  require_acknowledgement: false, status: 'active',
};

function formToPayload(form, file) {
  return {
    title: form.title.trim(),
    description: form.description.trim(),
    category: form.category.trim(),
    document_type: form.document_type,
    version: form.version.trim(),
    effective_date: form.effective_date || null,
    expiry_date: form.expiry_date || null,
    tags: form.tags.split(',').map((tag) => tag.trim()).filter(Boolean),
    require_acknowledgement: form.require_acknowledgement,
    status: form.status,
    file,
  };
}

// Admin CRUD for engineer_docs (update_tasks.md §7.2). Agency-only writes
// per existing rules; uploaded_by is stamped server-side by
// pb_hooks/engineer_docs.pb.js (TBC-4).
export default function Files() {
  const { data, add, update, remove } = useData();
  const { notify } = useToast();
  const [query, setQuery] = useState('');
  const [docType, setDocType] = useState('all');
  const [status, setStatus] = useState('all');
  const [editing, setEditing] = useState(null); // doc being edited, or 'new'
  const [form, setForm] = useState(EMPTY_FORM);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [deleting, setDeleting] = useState(null);

  const docs = useMemo(() => data.engineerDocs.filter((doc) => {
    if (docType !== 'all' && doc.document_type !== docType) return false;
    if (status !== 'all' && doc.status !== status) return false;
    if (!query) return true;
    const haystack = `${doc.title} ${doc.description} ${doc.category} ${(doc.tags || []).join(' ')} ${doc.uploaded_by}`.toLowerCase();
    return haystack.includes(query.toLowerCase());
  }), [data.engineerDocs, query, docType, status]);

  const openNew = () => {
    setForm(EMPTY_FORM);
    setFile(null);
    setEditing('new');
  };

  const openEdit = (doc) => {
    setForm({
      title: doc.title, description: doc.description, category: doc.category,
      document_type: doc.document_type, version: doc.version,
      effective_date: doc.effective_date, expiry_date: doc.expiry_date,
      tags: (doc.tags || []).join(', '),
      require_acknowledgement: doc.require_acknowledgement, status: doc.status,
    });
    setFile(null);
    setEditing(doc.id);
  };

  const save = async (event) => {
    event.preventDefault();
    if (!form.title.trim()) { notify('Give the document a title', 'error'); return; }
    if (form.effective_date && form.expiry_date && form.expiry_date < form.effective_date) {
      notify('Expiry date must be after the effective date', 'error');
      return;
    }
    if (editing === 'new' && !(file instanceof File)) { notify('Choose a file to upload', 'error'); return; }
    setBusy(true);
    try {
      if (editing === 'new') {
        await add('engineerDocs', formToPayload(form, file));
        notify('Document uploaded');
      } else {
        await update('engineerDocs', editing, formToPayload(form, file));
        notify('Document updated');
      }
      setEditing(null);
    } catch (err) {
      notify(err.message || 'Could not save the document', 'error');
    } finally {
      setBusy(false);
    }
  };

  const confirmDelete = async () => {
    try {
      await remove('engineerDocs', deleting.id);
      notify('Document deleted');
    } catch (err) {
      notify(err.message || 'Could not delete the document', 'error');
    } finally {
      setDeleting(null);
    }
  };

  return (
    <div className="standalone-page">
      <header className="simple-header">
        <div>
          <span className="eyebrow">Engineer</span>
          <h1>Files</h1>
          <p className="field-hint">
            {docs.length} document{docs.length === 1 ? '' : 's'} — uploads appear in the engineer portal immediately.
          </p>
        </div>
        <div className="header-actions">
          <button className="primary-button" onClick={openNew}><SafeIcon icon={Icons.Upload} /> Upload document</button>
        </div>
      </header>

      <div className="toolbar">
        <label className="search-input">
          <SafeIcon icon={Icons.Search} />
          <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search title, category or tags…" />
        </label>
        <select value={docType} onChange={(event) => setDocType(event.target.value)} className="engineer-filter" aria-label="Filter by document type">
          <option value="all">All types</option>
          {DOC_TYPES.map((type) => <option key={type} value={type}>{type.replace('_', ' ')}</option>)}
        </select>
        <select value={status} onChange={(event) => setStatus(event.target.value)} className="engineer-filter" aria-label="Filter by status">
          <option value="all">All statuses</option>
          {STATUSES.map((item) => <option key={item} value={item}>{item}</option>)}
        </select>
      </div>

      {!data.engineerDocs.length ? (
        <EmptyState title="No documents yet" message="Upload policy PDFs, parts lists and manuals for your engineers." />
      ) : !docs.length ? (
        <EmptyState title="No matching documents" message="Try different filters or search." />
      ) : (
        <div className="docs-grid">
          {docs.map((doc) => (
            <article className="doc-card" key={doc.id}>
              <div className="card-top">
                <span className="eyebrow">{doc.document_type.replace('_', ' ')}</span>
                {doc.status !== 'active' && <span className="status-pill">{doc.status}</span>}
              </div>
              <h3>{doc.title}</h3>
              {doc.description ? <p className="field-hint">{doc.description}</p> : null}
              <small className="field-hint">
                v{doc.version || '—'} · {doc.uploaded_by || '—'} · {date(doc.created)}
              </small>
              <div className="form-actions">
                <button className="link-button" onClick={() => openProtectedFile({ id: doc.id, collectionName: 'engineer_docs' }, doc.file)}>
                  <SafeIcon icon={Icons.Download} /> Download
                </button>
                <button className="link-button" onClick={() => openEdit(doc)}>Edit</button>
                <button className="link-button" onClick={() => setDeleting(doc)}>Delete</button>
              </div>
            </article>
          ))}
        </div>
      )}

      <SlideOver open={editing !== null} title={editing === 'new' ? 'Upload document' : 'Edit document'} onClose={() => setEditing(null)}>
        <form className="drawer-form" onSubmit={save}>
          <label>Title<input value={form.title} onChange={(event) => setForm((f) => ({ ...f, title: event.target.value }))} placeholder="e.g. Worcester boiler manual" required /></label>
          <label>Description<textarea rows="2" value={form.description} onChange={(event) => setForm((f) => ({ ...f, description: event.target.value }))} /></label>
          <div className="form-row">
            <label>Category<input value={form.category} onChange={(event) => setForm((f) => ({ ...f, category: event.target.value }))} placeholder="e.g. Boilers" /></label>
            <label>Version<input value={form.version} onChange={(event) => setForm((f) => ({ ...f, version: event.target.value }))} placeholder="1.0" /></label>
          </div>
          <label>Document type
            <select value={form.document_type} onChange={(event) => setForm((f) => ({ ...f, document_type: event.target.value }))}>
              {DOC_TYPES.map((type) => <option key={type} value={type}>{type.replace('_', ' ')}</option>)}
            </select>
          </label>
          <div className="form-row">
            <label>Effective date<input type="date" value={form.effective_date} onChange={(event) => setForm((f) => ({ ...f, effective_date: event.target.value }))} /></label>
            <label>Expiry date<input type="date" value={form.expiry_date} onChange={(event) => setForm((f) => ({ ...f, expiry_date: event.target.value }))} /></label>
          </div>
          <label>Tags<input value={form.tags} onChange={(event) => setForm((f) => ({ ...f, tags: event.target.value }))} placeholder="comma, separated" /></label>
          <label className="check-row"><input type="checkbox" checked={form.require_acknowledgement} onChange={(event) => setForm((f) => ({ ...f, require_acknowledgement: event.target.checked }))} /> Require engineer acknowledgement</label>
          <label>Status
            <select value={form.status} onChange={(event) => setForm((f) => ({ ...f, status: event.target.value }))}>
              {STATUSES.map((item) => <option key={item} value={item}>{item}</option>)}
            </select>
          </label>
          <FileDropzone
            onFiles={(files) => setFile(files[0] || null)}
            accept={FILE_ACCEPT}
            multiple={false}
            hint="PDF, Word, CSV or Excel — up to 10 MB"
            onReject={(msgs) => notify(msgs.join('. '), 'error')}
          />
          <div className="drawer-actions">
            <button type="button" className="secondary-button" onClick={() => setEditing(null)}>Cancel</button>
            <button className="primary-button" disabled={busy}>{busy ? 'Saving…' : editing === 'new' ? 'Upload' : 'Save changes'}</button>
          </div>
        </form>
      </SlideOver>

      <ConfirmDialog
        open={Boolean(deleting)}
        title="Delete document?"
        message={deleting ? `"${deleting.title}" will be removed for engineers immediately.` : ''}
        confirmLabel="Delete"
        onConfirm={confirmDelete}
        onClose={() => setDeleting(null)}
      />
    </div>
  );
}
